import { formatDate } from '@angular/common';
import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

export class DateUtils {
  static utcDateFormat = 'UTC';

  static currentLocale(): string {
    //TODO change the locale if using french language
    return 'en-CA';
  }

  static ngbDateToDate(ngbDate: NgbDate | null | undefined): Date | null {
    if (!ngbDate) {
      return null;
    }
    return new Date(Date.UTC(ngbDate.year, ngbDate.month - 1, ngbDate.day));
  }

  static dateToNgbDate(date: Date | null | undefined): NgbDate | null {
    if (!date) {
      return null;
    }
    const ngbDateStruct: NgbDateStruct = {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1, // Month is 0-indexed in Date, 1-indexed in NgbDateStruct
      day: date.getUTCDate(),
    };
    return NgbDate.from(ngbDateStruct);
  }

  static isoToDateStringFormat(): string {
    return $localize`MMMM d, yyyy`;
  }

  static isoToDateString(isoDateString?: string, utcDate: boolean = false): string {
    if (!isoDateString) {
      return '';
    }
    const timeZone = utcDate ? 'UTC' : 'local';
    return formatDate(isoDateString, this.isoToDateStringFormat(), this.currentLocale(), timeZone);
  }

  static isoToShorthandDateStringFormat(): string {
    return $localize`MMM d, yyyy`;
  }

  static isoToShorthandDateString(isoDateString?: string, utcDate: boolean = false): string {
    if (!isoDateString) {
      return '';
    }
    const timeZone = utcDate ? 'UTC' : 'local';
    return formatDate(isoDateString, this.isoToShorthandDateStringFormat(), this.currentLocale(), timeZone);
  }

  static isoToShorthandDateTimeStringFormat(): string {
    return $localize`MMM d, yyyy 'at' h:mm a`;
  }

  static isoToShorthandDayStartFormat(timeZoneSuffix?: string): string {
    return timeZoneSuffix
      ? $localize`MMM d, yyyy 'at 12:00 AM ${timeZoneSuffix}'`
      : $localize`MMM d, yyyy 'at 12:00 AM'`;
  }

  static isoToShorthandDayEndFormat(timeZoneSuffix?: string): string {
    return timeZoneSuffix
      ? $localize`MMM d, yyyy 'at 11:59 PM ${timeZoneSuffix}'`
      : $localize`MMM d, yyyy 'at 11:59 PM'`;
  }

  static isoToShorthandDateTimeString(isoDateString?: string, utcDate: boolean = false): string {
    if (!isoDateString) {
      return '';
    }
    const timeZone = utcDate ? 'UTC' : 'local';
    return formatDate(isoDateString, this.isoToShorthandDateTimeStringFormat(), this.currentLocale(), timeZone);
  }

  static dateToFullDateStringFormat(): string {
    return $localize`MMMM d, yyyy`;
  }

  static dateToFullDateString(date: Date | null | undefined, utcDate: boolean = false): string {
    if (!date) {
      return '';
    }
    const timeZone = utcDate ? 'UTC' : 'local';
    return formatDate(date, this.dateToFullDateStringFormat(), this.currentLocale(), timeZone);
  }

  static dateToInputDateStringFormat(): string {
    return $localize`dd/MM/yyyy`;
  }

  static dateToInputDateString(date: Date | null | undefined, utcDate: boolean = false): string {
    if (!date) {
      return '';
    }
    const timeZone = utcDate ? 'UTC' : 'local';
    return formatDate(date, this.dateToInputDateStringFormat(), this.currentLocale(), timeZone);
  }

  static dateToIsoDateOnlyFormat(): string {
    return $localize`yyyy-MM-dd`;
  }

  static dateToIsoDateOnly(date: Date | null | undefined, utcDate: boolean = false): string {
    if (!date) {
      return '';
    }
    const timeZone = utcDate ? 'UTC' : 'local';
    return formatDate(date, this.dateToIsoDateOnlyFormat(), this.currentLocale(), timeZone).split('T')[0];
  }

  static startOfDate(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
  }

  static endOfDate(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
  }

  // null | undefined uses today's date
  static addDaysToUtcDateOnly(dateOnly: string | Date | null | undefined, numberOfDays: number): string {
    let date: Date;
    if (dateOnly && dateOnly instanceof Date) {
      date = new Date(dateOnly);
    } else {
      dateOnly = dateOnly ? dateOnly : new Date().toISOString();
      // forcing everything to use beginning of day
      date = new Date(dateOnly.split('T')[0] + 'T00:00:00Z');
    }

    date.setUTCDate(date.getUTCDate() + numberOfDays);

    return date.toISOString().split('T')[0];
  }

  static fromDateOnlyToDate(dateOnly: string | null | undefined): Date | null {
    return !!dateOnly && Date.parse(dateOnly) ? new Date(`${dateOnly}T00:00`) : null;
  }

  static formatDateRange(from: string | null | undefined, to: string | null | undefined): string | null {
    const fromDate = DateUtils.fromDateOnlyToDate(from);
    const toDate = DateUtils.fromDateOnlyToDate(to);
    if (!fromDate || !toDate) return null;
    const format = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    /* designers wants no spaces around the en dash so we gotta remove them,
       only problem is that some browsers put spacebar and some put hair space…
       so just match all whitespace around the en dash */
    return format.formatRange(fromDate, toDate).replace(/\s*–\s*/g, '–');
  }

  static compareDateOnly(date1: string | null | undefined, date2: string | null | undefined): number {
    if (!date1 || !date2) return 0;
    if (date1 === date2) return 0;
    if (date1 < date2) return -1;
    return 1;
  }

  static todayDateOnly(timeZone = 'America/Toronto'): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }
}
